import { useRef } from "react";

import { Query } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { CaseResponse } from "src/clients/case-management";

export const CASE_POLL_INTERVAL_MS = 1000 * 5;
export const QUEUED_ACTION_POLL_INTERVAL_MS = 1000;
export const QUEUED_ACTION_MAX_FAST_POLLS = 60;

export type CasePollInterval = (
  query: Query<CaseResponse, AxiosError>,
) => number | false;

const getQueuedActionId = (
  caseResponse: CaseResponse | undefined,
): string | undefined =>
  caseResponse?.included?.find(
    (resource) =>
      resource.type === "running_actions" &&
      resource.attributes.status === "queued",
  )?.id;

export const useCasePollInterval = (): CasePollInterval => {
  const fastWindow = useRef<{
    actionId: string;
    openedAtUpdateCount: number;
  } | null>(null);

  return (query) => {
    if (query.state.error) return false;

    const queuedActionId = getQueuedActionId(query.state.data);

    if (queuedActionId === undefined) {
      fastWindow.current = null;
      return CASE_POLL_INTERVAL_MS;
    }

    const { dataUpdateCount } = query.state;
    const openWindow = fastWindow.current;
    const openedAtUpdateCount =
      openWindow && openWindow.actionId === queuedActionId
        ? openWindow.openedAtUpdateCount
        : dataUpdateCount;

    fastWindow.current = { actionId: queuedActionId, openedAtUpdateCount };

    return dataUpdateCount - openedAtUpdateCount < QUEUED_ACTION_MAX_FAST_POLLS
      ? QUEUED_ACTION_POLL_INTERVAL_MS
      : CASE_POLL_INTERVAL_MS;
  };
};
